// angular dependencies
import {
  ENVIRONMENT_INITIALIZER,
  inject,
  isDevMode,
  NgModule,
  Provider,
} from '@angular/core';
import {
  DateFnsAdapter,
  MAT_DATE_FNS_FORMATS,
  MatDateFnsModule,
} from '@angular/material-date-fns-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
// fantaz dependencies
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';

// third-party dependencies
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  provideTanStackQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import enUS from 'date-fns/locale/en-US';
import { del, get, set } from 'idb-keyval';
import {
  RECAPTCHA_BASE_URL,
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaV3Module,
} from 'ng-recaptcha-2';
import { provideNgxMask } from 'ngx-mask';

import {
  environment,
  getAppEnvironmentConfigProvider,
} from '@fantaz/shared/app-config';
import { SharedAuthModule } from '@fantaz/shared/auth';
import {
  FzGoogleTagManagerService,
  FzZendeskService,
  provideDataAccess,
  provideDataAccessConfig,
} from '@fantaz/shared/data-access/api';
import { provideEventBus } from '@fantaz/shared/infrastructure';
import { SharedSnackbarModule } from '@fantaz/shared/snackbar';
import { SharedUiDialogMessageModule } from '@fantaz/shared/ui/dialog-message';
import { MaterialModule } from '@fantaz/shared/ui/material';
import { SharedUtilsErrorModule } from '@fantaz/shared/utils/errors';

import { GoogleAnalyticModule } from './analytic/google-analytic.module';
import { extModules } from './build-specifics';
import { appRoutes } from './config';
import { appMessages } from './config/app-messages.config';
import { provideFeatureFlags } from './features';
import { I18NModule } from './i18n';
import { provideAppMessages } from './messages';
import { CoreStoreRouterModule, provideAppRouterPaths } from './router';
import { NgrxModule } from './state';
import { PwaService } from './utils/pwa.service';
import { ZendeskModule } from './zendesk';

// ********************************************************************************
// *** TanStack Query Configuration
// ********************************************************************************
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 1, // 1 hour
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const asyncPersister = createAsyncStoragePersister({
  key: 'fantaz-db-cache',
  storage: {
    getItem: (key) => get(key),
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
});

persistQueryClient({
  queryClient: queryClient,
  persister: asyncPersister,
  buster: 'v1.0.0',
  maxAge: 1000 * 60 * 60 * 1, // 1 hour
});

// ********************************************************************************
// *** Provide Core
// ********************************************************************************
export const provideCore = (): Provider[] => {
  return [
    //
    // Setup app messages
    //
    provideAppMessages({
      paths: {
        useValue: appMessages,
      },
    }),

    //
    // Setup app routes paths
    //
    provideAppRouterPaths({
      paths: {
        useValue: appRoutes,
      },
    }),

    //
    // Setup environment
    //
    getAppEnvironmentConfigProvider(),

    // Initializes
    // The environment injector is a generalized version of NgModuleRef, which
    // is also known as the “module injector”. It’s one of the primitives
    // required to support standalone components APIs introduced in Angular v14.
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue() {
        //
        const translate = inject(TranslateService);
        const zendesk = inject(FzZendeskService);
        const gtm = inject(FzGoogleTagManagerService);
        const pwa = inject(PwaService);

        // initialize translate
        translate.addLangs(['en-US', 'es']);
        translate.setDefaultLang('en-US');

        const browserLang = translate.getBrowserLang();
        if (browserLang?.match(/en|es/)) {
          translate.use(browserLang === 'en' ? 'en-US' : 'es');
        } else {
          translate.use('en-US');
        }

        // initialize zendesk config
        zendesk.init();

        // initialize google tag manager
        gtm.addGtmToDom();

        pwa.init();
      },
    },

    // Provides a class for centralized exception handling.
    // The default implementation of `ErrorHandler` prints error messages to the
    // `console`. To intercept error handling, write a custom exception handler
    // that replaces this default as appropriate for your app.
    // ...(isDevMode()
    //   ? []
    //   : [
    //       {
    //         provide: ErrorHandler,
    //         useClass: FantazErrorHandler,
    //       },
    //     ]),

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'always', appearance: 'fill' },
    },

    //
    // Add default options for all dialogs
    //
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        maxWidth: '95vw',
        maxHeight: '95vh',
      },
    },

    //
    // Date picker configuration
    //
    { provide: MAT_DATE_LOCALE, useValue: enUS },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_DATE_FNS_FORMATS,
    },

    //
    // Tab configuration
    //
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        stretchTabs: false,
      },
    },

    // reCaptcha configuration
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.reCaptcha.siteKeyV3,
    },
    {
      provide: RECAPTCHA_BASE_URL,
      useValue: 'https://www.google.com/recaptcha/api.js?badge=bottomleft',
    },

    provideEventBus(),
  ];
};

@NgModule({
  exports: [
    SharedAuthModule,
    BrowserAnimationsModule,
    BrowserModule,
    CoreStoreRouterModule,
    GoogleAnalyticModule,
    I18NModule,
    MatDateFnsModule,
    MaterialModule,
    NgrxModule,
    RouterModule,
    SharedUiDialogMessageModule,
    SharedUtilsErrorModule,
    StoreModule,
    ZendeskModule,
    SharedSnackbarModule,
  ],
  imports: [
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~ angular core modules
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    BrowserModule,
    BrowserAnimationsModule,
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~ community modules
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    MatDateFnsModule,
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~ fantaz core modules
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ZendeskModule,
    NgrxModule,
    I18NModule,
    GoogleAnalyticModule,
    CoreStoreRouterModule,
    RecaptchaV3Module,
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~ shared modules
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ApiDataAccessModule,
    MaterialModule,
    SharedUtilsErrorModule,
    SharedAuthModule,
    SharedUiDialogMessageModule,
    SharedSnackbarModule,
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~ Dev modules
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ...extModules,
  ],
  providers: [
    provideCore(),
    provideDataAccess(),
    provideDataAccessConfig(),
    provideFeatureFlags(),
    provideNgxMask({
      validation: true,
    }),

    provideTanStackQuery(
      queryClient,
      withDevtools(() => ({
        loadDevtools: isDevMode(),
        position: 'bottom',
        buttonPosition: 'bottom-left',
      })),
    ),
  ],
})
export class CoreModule {}
